import {
  model,
  Model,
  prop,
  idProp,
  rootRef,
  detach,
  modelAction,
  findParent,
  modelFlow,
  _async,
} from "mobx-keystone";
import { validateSolanaAddressNative } from "../helpers/solana";
import { Bob } from "./Bob";
import { sendToBobController } from "../../helpers/requests";
import { notifications } from "@mantine/notifications";

// USDT on Solana uses 6 decimal places
const USDT_DECIMALS = 6;
const USDT_BASE_UNITS_PER_DOLLAR = 10 ** USDT_DECIMALS; // 1_000_000

@model("exrails/SolanaUsdtProposal")
export class SolanaUsdtProposal extends Model({
  _version: prop(0),
  id: idProp,
  // Address fields
  proposedAddress: prop<string | null>(null),
  userFacingAddressErrorMessage: prop<string | null>(null),
  validProposedAddress: prop<string | null>(null),
  // Amount fields (in USDT base units, i.e., 1 USDT = 1_000_000 base units)
  proposedAmountBaseUnits: prop<number | null>(null),
  proposedAmountBaseUnitsIsValid: prop<boolean>(false),
  userFacingProposedAmountErrorMessage: prop<string | null>(null),
  // Quote fields from server
  uuidFromServer: prop<string | null>(null),
  amountSatoshisFromServer: prop<number | null>(null),
  platformFeeSatoshisFromServer: prop<number | null>(null),
  quoteExpiresAt: prop<number | null>(null), // Unix timestamp in ms
  // 2FA
  userProvided2FaCode: prop<string | null>(null),
  // Transaction result
  confirmedTransactionIdFromServer: prop<string | null>(null),
}) {
  @modelFlow
  sendSolanaUsdtPayment = _async(function* (this: SolanaUsdtProposal) {
    this.bob.setAreWeWaitingOnRails(true);
    const message = {
      bobUuid: this.bob.uuid,
      stablePaymentQuoteUuid: this.uuidFromServer!,
      twoFactorCode: this.userProvided2FaCode,
    };
    sendToBobController(message, "/bob_life/send_solana_usdt_payment")
      .then((response) => {
        if (response.success === false) {
          notifications.show({
            title: "Error",
            message: response.message,
            color: "red",
          });
          this.bob.setAreWeWaitingOnRails(false);
          return;
        }

        if (response.transactionId) {
          this.setConfirmedTransactionId(response.transactionId);
        }
        if (response.stablePayments) {
          this.bob.setOurStablePayments(response.stablePayments);
        }
        this.bob.workspace.makeEvent(
          "USDT Payment Sent",
          `A USDT payment of $${(this.proposedAmountBaseUnits! / USDT_BASE_UNITS_PER_DOLLAR).toFixed(2)} has been sent to ${this.validProposedAddress?.slice(0, 10)}...`,
          "usdt-payment-sent",
          null,
          response.transactionId,
        );
        this.bob.setAreWeWaitingOnRails(false);
      })
      .catch((error) => {
        this.bob.setAreWeWaitingOnRails(false);
        console.log(error);
      });
  });

  @modelFlow
  makeNewStablePaymentQuote = _async(function* (this: SolanaUsdtProposal) {
    this.bob.setAreWeWaitingOnRails(true);
    this.bob.checkIfWeNeedToShowTwoFactorForm();
    const message = {
      bobUuid: this.bob.uuid,
      destinationAddress: this.validProposedAddress!,
      amountBaseUnits: this.proposedAmountBaseUnits!,
      kind: "solana_usdt",
    };
    sendToBobController(
      message,
      "/bob_life/maybe_make_bob_stable_payment_quote",
    )
      .then((response) => {
        if (response.success === false) {
          notifications.show({
            title: "Error",
            message: response.message,
            color: "red",
          });
          this.bob.setAreWeWaitingOnRails(false);
          return;
        }

        const quoteData = response.quoteData;
        if (quoteData.amountBaseUnits !== this.proposedAmountBaseUnits) {
          notifications.show({
            title: "Error",
            message:
              "The amount returned by the server does not match the proposed amount. Please try again.",
            color: "red",
          });
          this.bob.setAreWeWaitingOnRails(false);
          return;
        }
        if (quoteData.destinationAddress !== this.validProposedAddress) {
          notifications.show({
            title: "Error",
            message:
              "The address returned by the server does not match the proposed address. Please try again.",
            color: "red",
          });
          this.bob.setAreWeWaitingOnRails(false);
          return;
        }

        this.setConfirmedQuote(
          quoteData.platformFeeSatoshis,
          quoteData.amountSatoshis,
          quoteData.uuid,
          quoteData.expiresAt,
        );
        this.bob.setAreWeWaitingOnRails(false);
      })
      .catch((error) => {
        this.bob.setAreWeWaitingOnRails(false);
        console.log(error);
      });
  });

  @modelAction
  setConfirmedQuote(
    platformFeeSatoshis: number,
    amountSatoshis: number,
    uuid: string,
    expiresAt: number,
  ) {
    this.platformFeeSatoshisFromServer = platformFeeSatoshis;
    this.amountSatoshisFromServer = amountSatoshis;
    this.uuidFromServer = uuid;
    this.quoteExpiresAt = expiresAt;
    this.bob.setAreWeWaitingOnRails(false);
  }

  @modelAction
  setConfirmedTransactionId(transactionId: string) {
    this.confirmedTransactionIdFromServer = transactionId;
  }

  @modelAction
  setUserProvided2FaCode(code: string | null) {
    this.userProvided2FaCode = code;
  }

  @modelAction
  setProposedAddress(address: string) {
    this.proposedAddress = address;
    const validation = validateSolanaAddressNative(address);
    if (validation.ok) {
      this.userFacingAddressErrorMessage = null;
      this.validProposedAddress = address.trim();
    } else {
      this.userFacingAddressErrorMessage = validation.message;
      this.validProposedAddress = null;
    }
  }

  @modelAction
  setProposedAmountBaseUnits(amount: number | null) {
    this.proposedAmountBaseUnits = amount;

    if (amount === null || amount === undefined) {
      this.proposedAmountBaseUnitsIsValid = false;
      this.userFacingProposedAmountErrorMessage = null;
      return;
    }

    if (amount < 0) {
      this.proposedAmountBaseUnitsIsValid = false;
      this.userFacingProposedAmountErrorMessage = "Amount cannot be negative.";
      return;
    }

    const minAmount =
      this.bob.workspace.minStablecoinPaymentSizeBaseUnits || 1_000_000;
    if (amount < minAmount) {
      this.proposedAmountBaseUnitsIsValid = false;
      this.userFacingProposedAmountErrorMessage = `Amount must be at least $${(minAmount / USDT_BASE_UNITS_PER_DOLLAR).toFixed(2)} USDT.`;
      return;
    }

    // Check against user's safe balance
    // We need to calculate how many satoshis this would cost
    // Using a rough estimate: amount in USD * exchange rate
    const exchangeRates = this.bob.workspace.exchangeRates?.data;
    if (exchangeRates && exchangeRates.usd) {
      const btcPriceInUsd = exchangeRates.usd;
      const amountInUsd = amount / USDT_BASE_UNITS_PER_DOLLAR;
      // satoshis = (amountInUsd / btcPriceInUsd) * 100_000_000
      const estimatedSatoshis = Math.ceil(
        (amountInUsd / btcPriceInUsd) * 100_000_000,
      );
      // Add fee buffer using the actual platform fee percentage
      const feePercentage =
        Number(
          this.bob.workspace.weKeepThisPlatformFeePercentageForStablePayments,
        ) || 0.01;
      const estimatedWithFees = Math.ceil(
        estimatedSatoshis * (1 + feePercentage),
      );

      // console.log("[SolanaUsdtProposal] Amount validation:", {
      //   amountInUsd,
      //   btcPriceInUsd,
      //   estimatedSatoshis,
      //   feePercentage,
      //   feePercentagePercent: `${(feePercentage * 100).toFixed(2)}%`,
      //   estimatedWithFees,
      //   safeBalanceInSatoshis: this.bob.safeBalanceInSatoshis,
      //   wouldExceedBalance: estimatedWithFees > this.bob.safeBalanceInSatoshis,
      // });

      if (estimatedWithFees > this.bob.safeBalanceInSatoshis) {
        this.proposedAmountBaseUnitsIsValid = false;
        // Calculate max USD they can send based on their balance
        // Account for BOTH percentage fee AND fixed fee satoshis
        const fixedFeeSatoshis =
          this.bob.workspace.bobStableSolanaFixedPlatformFeeSatoshis || 0;
        const maxSatoshisForPayment = Math.floor(
          (this.bob.safeBalanceInSatoshis - fixedFeeSatoshis) /
            (1 + feePercentage),
        );
        const maxUsd = Math.max(
          0,
          (maxSatoshisForPayment / 100_000_000) * btcPriceInUsd,
        );
        // Subtract 1 cent as safety buffer for rounding
        const safeMaxUsd = Math.max(0, maxUsd - 0.1);
        this.userFacingProposedAmountErrorMessage = `Please try an amount below $${safeMaxUsd.toFixed(2)} USDT`;
        return;
      }
    }

    this.proposedAmountBaseUnitsIsValid = true;
    this.userFacingProposedAmountErrorMessage = null;
  }

  // Helper to get amount in USD (for display)
  get proposedAmountInUsd(): number | null {
    if (this.proposedAmountBaseUnits === null) return null;
    return this.proposedAmountBaseUnits / USDT_BASE_UNITS_PER_DOLLAR;
  }

  // Helper to check if quote has expired
  get isQuoteExpired(): boolean {
    if (!this.quoteExpiresAt) return false;
    return Date.now() > this.quoteExpiresAt;
  }

  // Time remaining on quote in seconds
  get quoteTimeRemainingSeconds(): number {
    if (!this.quoteExpiresAt) return 0;
    const remaining = Math.max(0, this.quoteExpiresAt - Date.now());
    return Math.ceil(remaining / 1000);
  }

  get bob() {
    return findParent<Bob>(this, (parentNode) => parentNode instanceof Bob)!;
  }
}

export interface SolanaUsdtProposalInterface {
  SolanaUsdtProposal: SolanaUsdtProposal;
}

export const SolanaUsdtProposalRef = rootRef<SolanaUsdtProposal>(
  "exrails/SolanaUsdtProposalRef",
  {
    onResolvedValueChange(ref, newThing, oldThing) {
      if (newThing && !oldThing) {
        detach(ref);
      }
    },
  },
);
